import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import AwsS3 from '@uppy/aws-s3';
import CONFIG from "utils/config";
import { useGetProjectQuery } from 'api/project/queries/useGetProjectQuery';
import useCreateBIMMutation from 'api/project/mutations/useCreateBIMMutation';
import { useWorkspaces } from 'contexts/WorkspacesContext';
import { useParams } from 'react-router-dom';
import { CreateBIMPayload } from 'utils/bim';

interface CreateBIMModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateBIMModal: React.FC<CreateBIMModalProps> = ({ open, onClose }) => {
  const { projectSlug } = useParams();
  const { activeWorkspace } = useWorkspaces();
  const { data: project } = useGetProjectQuery(projectSlug || "", activeWorkspace.id);

  const { mutate: createBIM, isPending: isCreatePending } = useCreateBIMMutation(project?.id || "");

  const [formData, setFormData] = useState({
    name: '',
    scale: 1,
    isLocal: true,
    isInAutodesk: false,
    s3Url: '',
    autodeskUrn: '',
  });

  const [selectedFile, setSelectedFile] = useState<{
    s3Name: string;
    uploaded: boolean;
  } | undefined>();

  const [uppy] = useState(() => new Uppy().use(AwsS3, {
    endpoint: `${CONFIG.TRANSFERS_API_URL}/companion`,
    shouldUseMultipart: true,
  }));

  uppy.setOptions({
    restrictions: {
      allowedFileTypes: ['.ifc', '.rvt', '.dwg', '.3ds', '.obj', '.fbx'],
      maxNumberOfFiles: 1,
    }
  });

  uppy.on("complete", (result) => {
    const { successful } = result;
    if (!successful || successful.length === 0) return;
    setSelectedFile((prev) => prev && {
      ...prev,
      uploaded: true
    });
  });

  uppy.on("file-added", (file) => {
    const fileName = file.name ? file.name.replace(/\s+/g, "-") : file.id;
    file.name = fileName;
    file.meta.s3Key = `projects/${project?.id}/bims/${fileName}`;
    
    // Auto-fill the name if it's empty
    if (!formData.name) {
      setFormData(prev => ({
        ...prev,
        name: fileName.replace(/\.[^/.]+$/, ""), // Remove file extension
      }));
    }

    setSelectedFile({
      s3Name: fileName,
      uploaded: false,
    });
  });

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'scale' ? parseFloat(event.target.value) || 1 : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      isLocal: !event.target.checked,
    }));
  };

  const createDisabled = Boolean(
    !formData.name || 
    formData.scale <= 0 || 
    (formData.isInAutodesk ? !formData.autodeskUrn.trim() : (!selectedFile || !selectedFile.uploaded))
  );

  const handleSubmit = () => {
    if (!formData.name) return;

    const payload: CreateBIMPayload = {
      name: formData.name,
      scale: formData.scale,
      isLocal: formData.isLocal,
    };

    if (formData.isInAutodesk) {
      payload.autodeskUrn = formData.autodeskUrn;
    } else {
      if (selectedFile) {
        payload.s3Path = selectedFile.s3Name;
      }
    }

    createBIM(payload, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      scale: 1,
      isLocal: true,
      isInAutodesk: false,
      s3Url: '',
      autodeskUrn: '',
    });
    setSelectedFile(undefined);
    uppy.clear();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New BIM</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            fullWidth
            required
          />

          <TextField
            label="Scale"
            type="number"
            value={formData.scale}
            onChange={handleInputChange('scale')}
            fullWidth
            required
            inputProps={{ step: 0.1, min: 0 }}
            helperText="Scale factor for the BIM model (default: 1.0)"
          />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isInAutodesk}
                  onChange={handleToggleChange}
                  color="primary"
                />
              }
              label={
                <Stack>
                  <Typography variant="body2">
                    {formData.isInAutodesk ? "Autodesk Platform" : "Local Storage"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isInAutodesk 
                      ? "BIM model is hosted on Autodesk Platform Services" 
                      : "BIM model is stored locally in S3"
                    }
                  </Typography>
                </Stack>
              }
            />
          </Box>

          {formData.isInAutodesk ? (
            <TextField
              label="Autodesk URN"
              value={formData.autodeskUrn}
              onChange={handleInputChange('autodeskUrn')}
              fullWidth
              required
              placeholder="Enter Autodesk model URN"
              helperText="The unique identifier for your model in Autodesk Platform Services"
            />
          ) : (
            <Dashboard 
              uppy={uppy}
              proudlyDisplayPoweredByUppy={false}
              theme="dark"
              style={{
                border: "#888 solid 2px",
                borderRadius: "6px",
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreatePending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createDisabled || isCreatePending}
          sx={{
            background: 'royalblue',
            '&:hover': {
              background: 'royalblue',
              opacity: 0.9,
            },
          }}
        >
          {isCreatePending ? 'Creating...' : 'Create BIM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBIMModal;
